import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    Button,
    Box,
    Grid,
    Paper,
    TextField,
    Alert
} from '@mui/material';

const FeedbackResolutionDialog = ({
    open,
    onClose,
    selectedFeedback,
    onSubmit,
    submitting,
    formatDate
}) => {
    const [adminResponse, setAdminResponse] = useState('');

    React.useEffect(() => {
        if (open && selectedFeedback) {
            setAdminResponse(selectedFeedback.response || '');
        }
    }, [open, selectedFeedback]);

    const handleSubmit = () => {
        if (adminResponse.trim()) {
            onSubmit(adminResponse);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: 3 }
            }}
        >
            <DialogTitle sx={{ pb: 2 }}>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#4caf50' }}>
                    ✅ Resolve Feedback
                </Typography>
            </DialogTitle>
            <DialogContent>
                {selectedFeedback && (
                    <Box>
                        {/* Feedback Details */}
                        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, backgroundColor: '#f8f9fa' }}>
                            <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                                📝 Feedback Details
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>User:</strong> {selectedFeedback.userDisplayName}
                                    </Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Type:</strong> {selectedFeedback.correct ? 'Positive' : 'Negative'}
                                    </Typography>
                                    <Typography variant="body2">
                                        <strong>Date:</strong> {formatDate(selectedFeedback.createdAt)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <Typography variant="body2" sx={{ mb: 1 }}>
                                        <strong>Classification ID:</strong> {selectedFeedback.classificationId}
                                    </Typography>
                                    {selectedFeedback.providedCategory && (
                                        <Typography variant="body2" sx={{ mb: 1 }}>
                                            <strong>Suggested Category:</strong> {selectedFeedback.providedCategory}
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                            <Typography variant="body2" sx={{ mt: 2, p: 2, background: 'white', borderRadius: 1 }}>
                                <strong>Message:</strong> {selectedFeedback.message}
                            </Typography>
                        </Paper>

                        {/* Admin Response */}
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            label="Admin Response *"
                            value={adminResponse}
                            onChange={(e) => setAdminResponse(e.target.value)}
                            placeholder="Provide a detailed response explaining how this feedback will be used to improve the AI model..."
                            sx={{ mb: 2 }}
                        />

                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                            <Typography variant="body2">
                                💡 <strong>Resolution Tips:</strong>
                                <br />• Thank the user for their feedback
                                <br />• Explain how their input helps improve the AI
                                <br />• For negative feedback, acknowledge the correction and explain next steps
                                <br />• For positive feedback, express appreciation and mention continued improvements
                            </Typography>
                        </Alert>
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ p: 3, pt: 1 }}>
                <Button
                    onClick={onClose}
                    disabled={submitting}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting || !adminResponse.trim()}
                    sx={{
                        background: 'linear-gradient(45deg, #4caf50, #81c784)',
                        fontWeight: 600,
                        px: 3
                    }}
                >
                    {submitting ? 'Resolving...' : 'Resolve Feedback'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default FeedbackResolutionDialog;
